import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useOrderWorkspaceSession } from '@/hooks/useOrderWorkspaceSession';
import { createReviewOwner } from '@/utils/reviewOwnership';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { isDesktopApp, isDesktopOffline } from '@/api/desktopSync';
import ConflictCard from '@/components/reconciliation/ConflictCard';
import DeleteRequestCard from '@/components/reconciliation/DeleteRequestCard';
import QueueCard from '@/components/reconciliation/QueueCard';
import OfflineRunCard from '@/components/reconciliation/OfflineRunCard';
import CachedRunProposalCard from '@/components/reconciliation/CachedRunProposalCard';
import WorklistReviewPreview from '@/components/reconciliation/WorklistReviewPreview';
import { useCachedOrderConflictReview } from '@/hooks/useCachedOrderConflictReview';
import { useOfflineDeleteReview } from '@/hooks/useOfflineDeleteReview';
import { useOfflineOrderReview } from '@/hooks/useOfflineOrderReview';
import { useOfflineDraftRunReview } from '@/hooks/useOfflineDraftRunReview';
import { useCachedRunProposalReview } from '@/hooks/useCachedRunProposalReview';
import { markReviewQueueDirty, subscribeToReviewQueueChanges } from '@/utils/reviewQueueEvents';
import { useRunSendRecovery } from '@/hooks/useRunSendRecovery';
import RunSendRecoveryPanel from '@/components/reconciliation/RunSendRecoveryPanel';

const REVIEW_STAGES = ['new-orders', 'order-changes', 'runs'];

const COMPLETION_MESSAGES = {
  offline_order_submitted: 'An offline-created order was sent to the cloud.',
  offline_order_discarded: 'An offline-created order was discarded from this computer.',
  offline_conflict_resolved: 'An offline order change was applied to the cloud order.',
  offline_conflict_discarded: 'An offline order change was discarded.',
  offline_delete_applied: 'An approved offline deletion removed the cloud order.',
  offline_delete_rejected: 'An offline deletion was discarded and the cloud order was kept.',
  offline_run_replayed: 'An offline-created run was sent to the cloud.',
  offline_run_discarded: 'An offline-created run was deleted from this computer.',
  cached_run_proposal_saved_as_new: 'An offline run plan was saved as a separate cloud draft.',
  cached_run_proposal_discarded: 'All offline changes for a cached run were discarded.',
  cached_run_proposal_synchronized: 'Reviewed cached-run changes were sent to the cloud.',
};

export default function ReconciliationView() {
  const session = useOrderWorkspaceSession();
  return <ReviewOpening key={session.key} session={session} />;
}

function ReviewOpening({ session }) {
  const [owner] = useState(() => createReviewOwner(session));
  const [, renderClaims] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [known, setKnown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [presentation, setPresentation] = useState('original');
  const [sessionCompletions, setSessionCompletions] = useState([]);
  const previousStageCountsRef = useRef(null);
  const unavailable = !isDesktopApp() || isDesktopOffline() || !session.valid;
  const isAdmin = session.isAdmin;


  const loadQueue = async () => {
    const read = owner.beginRead();
    if (!read || unavailable) { if (owner.current()) setLoading(false); return false; }
    setLoading(true);
    try {
      const [nextItems, nextConflicts, draftRuns, cachedRunItems, nextDeletes] = await Promise.all([
        queue.fetchItems(read), conflicts.fetchItems(read), runs.fetchItems(read), cachedRunProposals.fetchItems(read), deletes.fetchItems(read),
      ]);
      const refreshedConflicts = await conflicts.refreshEvidence(nextConflicts, read);
      const groups = [nextItems, refreshedConflicts, draftRuns, cachedRunItems, nextDeletes];
      if (!read.publish(groups)) throw new Error('Review observation unavailable');
      queue.setItems(nextItems); conflicts.setItems(refreshedConflicts); runs.setItems(draftRuns);
      cachedRunProposals.setItems(cachedRunItems); deletes.setItems(nextDeletes);
      void conflicts.loadUserNames(refreshedConflicts, read);
      setLoadError(false); setKnown(true);
      return true;
    } catch {
      if (read.current()) setLoadError(true);
      return false;
    } finally { if (read.current()) setLoading(false); }
  };

  // Each technical review workflow owns its own state and handlers, and is
  // given the screen's single loader so all four queues keep loading together.
  const queue = useOfflineOrderReview({ reload: loadQueue, isAdmin, owner });
  const pendingCount = useMemo(() => queue.items.length, [queue.items]);
  const conflicts = useCachedOrderConflictReview({ reload: loadQueue, owner });
  const conflictCount = useMemo(() => conflicts.items.length, [conflicts.items]);
  const runRecovery = useRunSendRecovery({ session, onSettled: () => { if (owner.current()) void loadQueue(); } });
  const runs = useOfflineDraftRunReview({ reload: loadQueue, owner, runRecovery });
  const cachedRunProposals = useCachedRunProposalReview({ reload: loadQueue, owner, runRecovery });
  const runCount = useMemo(
    () => runs.items.length + cachedRunProposals.items.length,
    [runs.items, cachedRunProposals.items],
  );
  const deletes = useOfflineDeleteReview({ reload: loadQueue, owner });
  const deleteCount = useMemo(() => deletes.items.length, [deletes.items]);
  const orderChangeCount = conflictCount + deleteCount;
  const stageCounts = useMemo(() => ({
    'new-orders': pendingCount,
    'order-changes': orderChangeCount,
    runs: runCount,
  }), [pendingCount, orderChangeCount, runCount]);
  const totalCount = pendingCount + orderChangeCount + runCount;

  useEffect(() => {
    if (loading || unavailable || totalCount === 0) return;

    if (!activeStage) {
      setActiveStage(REVIEW_STAGES.find(stage => stageCounts[stage] > 0));
      previousStageCountsRef.current = stageCounts;
      return;
    }

    const previousCounts = previousStageCountsRef.current;
    const selectedStageEmptied = previousCounts
      && previousCounts[activeStage] > 0
      && stageCounts[activeStage] === 0;

    if (selectedStageEmptied) {
      const currentIndex = REVIEW_STAGES.indexOf(activeStage);
      const nextStage = REVIEW_STAGES
        .slice(currentIndex + 1)
        .find(stage => stageCounts[stage] > 0)
        || REVIEW_STAGES
          .slice(0, currentIndex)
          .find(stage => stageCounts[stage] > 0);
      if (nextStage) setActiveStage(nextStage);
    }

    previousStageCountsRef.current = stageCounts;
  }, [activeStage, loading, stageCounts, totalCount, unavailable]);

  useEffect(() => {
    owner.activate();
    const unsubscribe = owner.subscribe(() => renderClaims(value => value + 1));
    void loadQueue();
    return () => { unsubscribe(); owner.retire(); };
    // One keyed opening owns all observations; StrictMode repeats this lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  useEffect(() => subscribeToReviewQueueChanges((event) => {
    if (!owner.current() || event.detail?.opening !== owner.opening) return;
    const reason = event.detail?.reason;
    const message = COMPLETION_MESSAGES[reason];
    if (!message) return;
    setSessionCompletions(previous => [
      { id: `${Date.now()}-${reason}`, message },
      ...previous,
    ].slice(0, 5));
  }), [owner]);

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="text-center">
          <h1 className="mb-2 text-3xl font-bold text-stone-900">Offline Order Review</h1>
          <p className="text-stone-600">
            Validate orders saved on this computer before syncing them to the cloud.
          </p>
        </div>

        {isDesktopApp() && session.valid && isAdmin && <RunSendRecoveryPanel recovery={runRecovery} offline={session.offline} />}

        {!unavailable && (
          <div className="flex justify-center" aria-label="Review presentation">
            {/* TCA-26081801: dual presentation is temporary until the owner selects the long-term Review UI. */}
            <div className="inline-flex rounded-lg border border-stone-300 bg-white p-1">
              <Button
                type="button"
                size="sm"
                variant={presentation === 'original' ? 'default' : 'ghost'}
                onClick={() => setPresentation('original')}
                aria-pressed={presentation === 'original'}
              >
                Original
              </Button>
              <Button
                type="button"
                size="sm"
                variant={presentation === 'worklist' ? 'default' : 'ghost'}
                onClick={() => setPresentation('worklist')}
                aria-pressed={presentation === 'worklist'}
              >
                Worklist Preview
              </Button>
            </div>
          </div>
        )}

        {queue.completed.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="py-4 text-sm text-emerald-900">
              {queue.completed.map((item, index) => (
                <div key={`${item.local}-${item.cloud}-${index}`}>
                  {item.local} &rarr; {item.cloud}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {owner.pending() && <p role="status">A Review action is still awaiting its result. Repeating that item is paused.</p>}
        {owner.holds().length > 0 && (
          <Card><CardContent className="space-y-3 py-4" role="status">
            <p>Some earlier Review actions remain unconfirmed. Those items are held to prevent an accidental repeat. Refreshing the queue does not prove they completed.</p>
            {owner.holds().map((claim, index) => <div key={claim.target}>
              <p>{claim.state === 'local-pending' ? 'Cloud accepted an action; this desktop still needs its local acknowledgement.' : 'An action may have taken effect, but its outcome is unknown.'}</p>
              {claim.state === 'local-pending' && !owner.canRetry(claim) && <p>The original session or cache is no longer available for this acknowledgement.</p>}
              {owner.canRetry(claim) && <Button onClick={async () => {
                const accepted = await owner.retry(claim);
                if (owner.current()) {
                  if (accepted) markReviewQueueDirty('review_queue_changed');
                  await loadQueue();
                }
              }}>Retry local acknowledgement {index + 1}</Button>}
            </div>)}
          </CardContent></Card>
        )}
        {!unavailable && loadError && known && <div role="alert">
          Current Review evidence could not be refreshed. Displayed items are last known; decisions are paused.
          <Button onClick={() => loadQueue()}>Retry Review load</Button>
        </div>}
        {unavailable ? (
          <Card>
            <CardContent className="py-8 text-center text-stone-500">
              Offline order review is available only in the desktop app while connected.
            </CardContent>
          </Card>
        ) : loading && !known ? (
          <Card>
            <CardContent className="py-8 text-center text-stone-500">
              <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
              Loading offline review queue...
            </CardContent>
          </Card>
        ) : loadError && !known ? (
          <Card><CardContent className="py-8 text-center" role="alert">
            <p>Review could not load current evidence. Saved work has not been declared empty or completed.</p>
            <Button onClick={() => loadQueue()}>Retry Review load</Button>
          </CardContent></Card>
        ) : totalCount === 0 ? (
          <Card data-testid="reconciliation-empty-state">
            <CardContent className="py-8 text-center text-stone-500">
              No offline work is waiting for review.
            </CardContent>
          </Card>
        ) : presentation === 'worklist' ? (
          <WorklistReviewPreview
            activeStage={activeStage || REVIEW_STAGES.find(stage => stageCounts[stage] > 0)}
            onStageChange={setActiveStage}
            stageCounts={stageCounts}
            newOrders={queue.items}
            orderConflicts={conflicts.items}
            deletionRequests={deletes.items}
            offlineRuns={runs.items}
            cachedRuns={cachedRunProposals.items}
            validations={queue.validations}
            duplicateAcknowledgments={queue.duplicateAcknowledgments}
            newOrderActions={queue}
            offlineRunActions={runs}
            cachedRunActions={cachedRunProposals}
            orderConflictActions={conflicts}
            deletionActions={deletes}
            isAdmin={isAdmin}
          />
        ) : (
          <Tabs value={activeStage || REVIEW_STAGES.find(stage => stageCounts[stage] > 0)} onValueChange={setActiveStage}>
            <TabsList className="grid h-auto w-full grid-cols-3" aria-label="Offline review stages">
              <TabsTrigger value="new-orders" data-testid="review-tab-new-orders">
                New Orders ({pendingCount})
              </TabsTrigger>
              <TabsTrigger value="order-changes" data-testid="review-tab-order-changes">
                Order Changes ({orderChangeCount})
              </TabsTrigger>
              <TabsTrigger value="runs" data-testid="review-tab-runs">
                Runs ({runCount})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new-orders" className="space-y-4" data-testid="review-stage-new-orders">
              {pendingCount === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-stone-500">
                    No new offline orders are waiting.
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>{pendingCount} offline order{pendingCount !== 1 ? 's' : ''} waiting</CardTitle>
                    <CardDescription>
                      Validation checks live cloud rules before creating the cloud order.
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}
              {queue.items.map(item => (
                <QueueCard
                  key={item.order.id}
                  item={item}
                  validation={queue.validations[item.order.id]}
                  validating={queue.validatingId === item.order.id}
                  submitting={queue.submittingId === item.order.id}
                  discarding={queue.discardingId === item.order.id}
                  numberConflict={queue.numberConflicts[item.order.id]}
                  manualNumber={queue.manualNumbers[item.order.id]}
                  duplicateAcknowledged={Boolean(queue.duplicateAcknowledgments[item.order.id])}
                  onDuplicateAcknowledgedChange={queue.onDuplicateAcknowledgedChange}
                  onManualNumberChange={queue.onManualNumberChange}
                  onValidate={queue.onValidate}
                  onSubmit={queue.onSubmit}
                  onDiscard={queue.onDiscard}
                />
              ))}
            </TabsContent>

            <TabsContent value="order-changes" className="space-y-4" data-testid="review-stage-order-changes">
              {orderChangeCount === 0 && (
                <Card>
                  <CardContent className="py-8 text-center text-stone-500">
                    No offline order changes are waiting.
                  </CardContent>
                </Card>
              )}
              {deleteCount > 0 && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>{deleteCount} deletion request{deleteCount !== 1 ? 's' : ''} waiting</CardTitle>
                      <CardDescription>
                        Orders an admin asked to delete while offline. Nothing has been
                        deleted yet.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  {deletes.items.map(item => (
                    <DeleteRequestCard
                      key={item.mutation.id}
                      item={item}
                      isAdmin={isAdmin}
                      resolvingId={deletes.resolvingId}
                      onApprove={deletes.onApprove}
                      onReject={deletes.onReject}
                    />
                  ))}
                </>
              )}
              {conflictCount > 0 && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>{conflictCount} offline conflict{conflictCount !== 1 ? 's' : ''} waiting</CardTitle>
                      <CardDescription>
                        These offline changes conflicted with cloud state and need admin resolution.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  {conflicts.items.map(item => (
                    <ConflictCard
                      key={item.mutation.id}
                      item={item}
                      isAdmin={isAdmin}
                      resolvingId={conflicts.resolvingId}
                      onUseOfflineChange={conflicts.onUseOfflineChange}
                      onDiscard={conflicts.onDiscard}
                      userNames={conflicts.userNames}
                    />
                  ))}
                </>
              )}
            </TabsContent>

            <TabsContent value="runs" className="space-y-4" data-testid="review-stage-runs">
              {runCount === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-stone-500">
                    No offline runs are waiting.
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>{runCount} offline run{runCount !== 1 ? 's' : ''} waiting</CardTitle>
                    <CardDescription>
                      Review cached-run changes and sync ready local runs after their offline orders have cloud IDs.
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}
              {runs.items.map(item => (
                <OfflineRunCard
                  held={runs.isHeld(item)}
                  key={item.run.id}
                  item={item}
                  isAdmin={isAdmin}
                  resolvingId={runs.resolvingId}
                  onRecheck={runs.onRecheck}
                  onRemoveStop={runs.onRemoveStop}
                  onSync={runs.onSync}
                  onDiscard={runs.onDiscard}
                  driverUsers={runs.driverUsers}
                  onAssignDriver={runs.onAssignDriver}
                  onUnassignDriver={runs.onUnassignDriver}
                />
              ))}
              {cachedRunProposals.items.map(item => (
                <CachedRunProposalCard
                  held={cachedRunProposals.isHeld(item)}
                  key={item.proposal.cloud_run_id}
                  item={item}
                  resolvingId={cachedRunProposals.resolvingId}
                  onDecision={cachedRunProposals.onDecision}
                  onDiscard={cachedRunProposals.onDiscard}
                  onSynchronize={cachedRunProposals.onSynchronize}
                  onRename={cachedRunProposals.onRename}
                  onAssignDriver={cachedRunProposals.onAssignDriver}
                  onUnassignDriver={cachedRunProposals.onUnassignDriver}
                  onRemoveStop={cachedRunProposals.onRemoveStop}
                  onSaveAsNew={cachedRunProposals.onSaveAsNew}
                  driverUsers={cachedRunProposals.driverUsers}
                  isAdmin={isAdmin}
                />
              ))}
            </TabsContent>
          </Tabs>
        )}

        {sessionCompletions.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50" data-testid="review-session-completions">
            <CardContent className="py-4 text-sm text-emerald-950" role="status" aria-live="polite">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Completed this session
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSessionCompletions([])}
                  aria-label="Dismiss completed review activity"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <ul className="space-y-1">
                {sessionCompletions.map(item => <li key={item.id}>{item.message}</li>)}
              </ul>
              <p className="mt-2 text-xs text-emerald-800">
                This notice lasts only until this page is closed or refreshed. It is not permanent Review History.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
